import { ReactComponent as CalendarIcon } from "@assets/icons/calendar.svg";
import { ReactComponent as CheckIcon } from "@assets/icons/checked.svg";
import { ReactComponent as HashtagIcon } from "@assets/icons/hashtag.svg";
import { ReactComponent as PennyIcon } from "@assets/icons/penny_icon.svg";
import { ReactComponent as PersonIcon } from "@assets/icons/person_slim.svg";
import BottomBar from "@components/BottomBars/BottomBar";
import BottomBarDesktop from "@components/BottomBars/BottomBarDesktop";
import CardInformation from "@components/Cards/CardInformation";
import UploadButton from "@components/Inputs/UploadButton";
import HeaderProgress from "@components/Progress/HeaderProgress";
import Loading from "@components/layout/Loading";
import { auth, storage } from "@utils/firebase";
import {
  useCurrentUserMutation,
  useGetUser,
  usePensionApplicationSteps,
} from "@utils/hooks";
import { ref as storageRef } from "firebase/storage";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useUploadFile } from "react-firebase-hooks/storage";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type VerifyIdentityProps = {};

const VerifyIdentity: React.FC<VerifyIdentityProps> = () => {
  const [uploadFile] = useUploadFile();
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const { mutate, isLoading: mutateLoading, isSuccess: mutateSuccess } = useCurrentUserMutation();
  const { currentStep, totalSteps, isLoading } = usePensionApplicationSteps();
  const { data: userData, isLoading: userDataLoading } = useGetUser();

  const [selectedFilePpsn, setSelectedFilePpsn] = useState<File>();
  const [selectedFileDateBirth, setSelectedFileDateBirth] = useState<File>();

  const [ppsnUploaded, setPpsnUploaded] = useState(false);
  const [datebirthUploaded, setDatebirthUploaded] = useState(false);

  // Add error states for file uploads
  const [ppsnUploadError, setPpsnUploadError] = useState<string>("");
  const [datebirthUploadError, setDatebirthUploadError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");

  const [verificationInProgress, setVerificationInProgress] = useState(false);

  const handleUpload = (
    e: ChangeEvent<HTMLInputElement>,
    uploadTo: "ppsn" | "datebirth"
  ) => {
    const typesUpload = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];

    const file = e.target.files ? e.target.files[0] : undefined;

    if (!file) return;

    const checker = typesUpload.some((type) => file.type === type);

    if (!checker) {
      const errorMessage = "Upload only these types: image/png, image/jpeg, image/jpg, application/pdf";
      
      if (uploadTo === "ppsn") {
        setPpsnUploadError(errorMessage);
      } else if (uploadTo === "datebirth") {
        setDatebirthUploadError(errorMessage);
      }
      return;
    }

    // Clear any existing errors when a valid file is selected
    if (uploadTo === "ppsn") {
      setPpsnUploadError("");
      setSelectedFilePpsn(file);
      setPpsnUploaded(true);
      return;
    }

    if (uploadTo === "datebirth") {
      setDatebirthUploadError("");
      setSelectedFileDateBirth(file);
      setDatebirthUploaded(true);
      return;
    }
  };

  const handleSubmit = async () => {
    if (userData && userData?.pension_applied_verify_identity) return;

    // Clear any existing submit errors
    setSubmitError("");

    const filesToUpload = [selectedFilePpsn, selectedFileDateBirth];

    if (!filesToUpload.every((item) => !!item)) {
      setSubmitError("Please upload both required documents before submitting.");
      return;
    }

    try {
      await Promise.all(filesToUpload.map(async (item, key) => {
        const fileNames = {
          0: "ppsn",
          1: "datebirth",
        };
        const type = item?.type.split("/")[1]!;

        const path = `${user?.uid}/verify_identity/${
          fileNames[key as keyof typeof fileNames]
        }`;

        await uploadFile(storageRef(storage, path), item!, {
          contentType: item?.type,
          customMetadata: {
            type: type,
          },
        });
      }));

      await mutate({
        pension_applied_verify_identity: true,
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      setSubmitError('Error uploading files. Please try again.');
    }
  };

  // Handle navigation after successful mutation
  useEffect(() => {
    if (!mutateLoading && mutateSuccess) {
      setVerificationInProgress(true);
      navigate('/pension-application');
    }
  }, [mutateLoading, mutateSuccess, navigate]);

  useEffect(() => {
    if (verificationInProgress || mutateLoading) {
      const timeout = setTimeout(() => {
        setVerificationInProgress(false);
      }, 3000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [mutateLoading, verificationInProgress, navigate]);

  useEffect(() => {
    if (!userDataLoading) {
      if (userData?.pension_applied_verify_identity) {
        setPpsnUploaded(true);
        setDatebirthUploaded(true);
      }
    }
  }, [userData, userDataLoading]);

  if (isLoading) {
    return <Loading />;
  }

  if (verificationInProgress || userDataLoading) {
    return (
      <Loading
        messages={{
          title: "We're verifying your info...",
          subtitle: "This shouldn't take long",
        }}
      />
    );
  }

  return (
    <section className="grow flex items-start md:items-center justify-center border-t-4 border-l-0 md:border-t-0 md:border-l-4 border-[#F4E34E] px-0 md:px-2 lg:px-0">
      <div className="max-w-full md:max-w-[880px] w-full md:rounded-3xl md:border md:border-pn_gray-200">
        <HeaderProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          onlyStep
          closeButtonEvent={() => navigate("/pension-application")}
        />

        <div className="flex items-start flex-col md:flex-row md:items-stretch">
          <div className="p-4 md:p-6 flex-1">
            <div className="p-0 md:p-6 flex flex-col gap-y-4 md:gap-y-6">
              <div className="flex items-center isolate">
                <div className="w-[60px] h-[60px] bg-pn_gray-lush flex items-center justify-center rounded-full">
                  <PersonIcon className="w-8 h-8" />
                </div>
                <div className="w-[60px] h-[60px] -z-10 -translate-x-4 bg-[#F4E34E] flex items-center justify-center rounded-full">
                  <PennyIcon className="w-6 h-[27px]" />
                </div>
              </div>

              <div className="flex flex-col gap-y-2">
                <span className="text-pn-large-title text-pn_gray-950">
                  {userData?.pension_applied_verify_identity
                    ? "We're verifying your identity"
                    : "Verify your identity"}
                </span>
                <span className="text-pn-body-large-regular text-pn_gray-600">
                  {userData?.pension_applied_verify_identity
                    ? "We are verifying your identity as Irish regulations require Penny to confirm your identity, soon you will be informed"
                    : "As a solo trader or company director you are entitled to different pension ratings and providing more information about your activity helps Penny design a better pension for you"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 w-full pt-0 md:pt-6 pl-0 md:pl-6 pr-0 md:pr-8 pb-0 md:pb-[99px]">
            <div className="p-4 flex flex-col gap-y-6">
              <div className="flex flex-col gap-y-2">
                <span className="text-pn-body-emphasis">
                  Required documents
                </span>
                <span className="text-pn-callout-regular text-pn_gray-600">
                  Please upload documents that are readable and are not older
                  than 6 months.
                </span>
              </div>

              <div className="flex flex-col gap-y-2 md:gap-y-4">
                <div className="flex items-center gap-x-4 border bg-pn_white border-pn_gray-200 rounded-2xl p-4">
                  <div
                    className={twMerge(
                      "rounded-full w-10 h-10 flex items-center justify-center",
                      ppsnUploaded ? "bg-pn_green-500" : "bg-pn_paper-3"
                    )}
                  >
                    {ppsnUploaded ? <CheckIcon /> : <HashtagIcon />}
                  </div>

                  <div className="flex items-start flex-1 flex-col gap-y-1">
                    <span className="text-pn-body-large-emphasis text-pn_gray-950 flex-1">
                      Proof of PPSN
                    </span>
                    <span className="text-pn-body-small-regular line-clamp-2 text-pn_gray-600">
                      A document that proves your PPSN number
                    </span>
                  </div>

                  {!ppsnUploaded && (
                    <UploadButton
                      accept="image/png,image/jpg,image/jpeg,application/pdf"
                      className="btn-primary-sm-40"
                      inputFor="file-ppsn"
                      label="Add"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpload(e, "ppsn")
                      }
                    />
                  )}
                </div>

                {ppsnUploadError && (
                  <div className="text-pn-body-small-regular text-red-500 mt-2 px-4">
                    {ppsnUploadError}
                  </div>
                )}

                <div className="flex items-center gap-x-4 border bg-pn_white border-pn_gray-200 rounded-2xl p-4">
                  <div
                    className={twMerge(
                      "rounded-full w-10 h-10 flex items-center justify-center",
                      datebirthUploaded ? "bg-pn_green-500" : "bg-pn_paper-3"
                    )}
                  >
                    {datebirthUploaded ? <CheckIcon /> : <CalendarIcon />}
                  </div>

                  <div className="flex items-start flex-1 flex-col gap-y-1">
                    <span className="text-pn-body-large-emphasis text-pn_gray-950 flex-1">
                      Proof of Identity
                    </span>
                    <span className="text-pn-body-small-regular line-clamp-2 text-pn_gray-600">
                      A certificate that displays your birthday e.g identity
                      card
                    </span>
                  </div>

                  {!datebirthUploaded && (
                    <UploadButton
                      accept="image/png,image/jpg,image/jpeg,application/pdf"
                      className="btn-primary-sm-40"
                      inputFor="file-datebirth"
                      label="Add"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpload(e, "datebirth")
                      }
                    />
                  )}
                </div>

                {datebirthUploadError && (
                  <div className="text-pn-body-small-regular text-red-500 mt-2 px-4">
                    {datebirthUploadError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="block md:hidden px-4">
          <CardInformation
            variant="safe"
            description={
              <div className="text-callout-regular">
                Your information is always safe with Penny.{" "}
                <Link to="#" className="text-pn-callout-link text-pn_cyan-500">
                  Learn more about how we do it.
                </Link>
              </div>
            }
          />
        </div>

        {submitError && (
          <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-pn-body-small-regular text-red-600">
              {submitError}
            </div>
          </div>
        )}
        <div className="mt-[73px] block md:hidden">
          <BottomBar
            labelBack="Back"
            labelNext="Submit"
            onBack={() => navigate("/pension-application")}
            layout="side_by_side"
            onNext={handleSubmit}
            disabledBackBtn={
              !datebirthUploaded ||
              userData?.pension_applied_verify_identity
            }
            disabledNextBtn={
              !ppsnUploaded ||
              !datebirthUploaded ||
              userData?.pension_applied_verify_identity
            }
          />
        </div>

        {submitError && (
          <div className="hidden md:block mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-pn-body-small-regular text-red-600">
              {submitError}
            </div>
          </div>
        )}

        <div className="hidden md:block">
          <BottomBarDesktop
            message="Your information is always secure with Penny"
            labelBack="Back"
            labelNext="Submit"
            insideCard
            disableBack={mutateLoading}
            disableNext={
              !ppsnUploaded ||
              !datebirthUploaded ||
              userData?.pension_applied_verify_identity
            }
            onBack={() => navigate("/pension-application")}
            onNext={handleSubmit}
          />
        </div>

        {/* <div className="p-4 flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-2">
            <span className="text-pn-body-emphasis">Required documents</span>
            <span className="text-pn-callout-regular text-pn_gray-600">
              Please upload documents that are readable and are not older than 6
              months.
            </span>
          </div>

          <div className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-3 border bg-pn_white border-pn_gray-200 rounded-2xl p-4">
              <div
                className={twMerge(
                  "rounded-full w-10 h-10 flex items-center justify-center",
                  ppsnUploaded ? "bg-pn_green-500" : "bg-pn_paper-3"
                )}
              >
                {ppsnUploaded ? <CheckIcon /> : <HashtagIcon />}
              </div>
              <div className="flex items-center flex-1">
                <span className="text-pn-body-regular flex-1">
                  Proof of PPSN
                </span>
                {!ppsnUploaded && (
                  <UploadButton
                    accept="image/png,image/jpg,image/jpeg,application/pdf"
                    className="btn-primary-sm"
                    inputFor="file-ppsn"
                    label="Add"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpload(e, "ppsn")
                    }
                  />
                )}
              </div>
            </div>

            <div className="flex items-center gap-x-3 border bg-pn_white border-pn_gray-200 rounded-2xl p-4">
              <div
                className={twMerge(
                  "rounded-full w-10 h-10 flex items-center justify-center",
                  datebirthUploaded ? "bg-pn_green-500" : "bg-pn_paper-3"
                )}
              >
                {datebirthUploaded ? <CheckIcon /> : <CalendarIcon />}
              </div>
              <div className="flex items-center flex-1">
                <span className="text-pn-body-regular flex-1">
                  Proof of Identity
                </span>
                {!datebirthUploaded && (
                  <UploadButton
                    accept="image/png,image/jpg,image/jpeg,application/pdf"
                    className="btn-primary-sm"
                    inputFor="file-datebirth"
                    label="Add"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpload(e, "datebirth")
                    }
                  />
                )}
              </div>
            </div>
          </div>

          <CardInformation
            variant="safe"
            description={
              <div className="text-callout-regular">
                Your information is always safe with Penny.{" "}
                <Link to="#" className="text-pn-callout-link text-pn_cyan-500">
                  Learn more about how we do it.
                </Link>
              </div>
            }
          />
        </div>
        <BottomBar
          onBack={"/pension-application"}
          onNext={handleSubmit}
          disabledNextBtn={
            !ppsnUploaded ||
            !datebirthUploaded ||
            userData?.pension_applied_verify_identity
          }
          layout="main_back"
          labelNext="Submit"
        /> */}
      </div>
    </section>
  );
};
export default VerifyIdentity;
